import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Users, TrendingUp, Clock, ChefHat, AlertCircle, Calendar as CalendarIcon, Sparkles, Cloud, CloudRain, Sun, CloudSnow, CloudLightning, CloudDrizzle, CloudFog } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Badge } from "components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "api/apiClient";
import { getCategoryColorClass } from "utils";

/**
 * Map WMO weather code to a Lucide icon component.
 */
function getWeatherIcon(code, className = "w-4 h-4") {
  if (code === 0) return <Sun className={className} />;
  if (code >= 1 && code <= 3) return <Cloud className={className} />;
  if (code >= 45 && code <= 48) return <CloudFog className={className} />;
  if (code >= 51 && code <= 57) return <CloudDrizzle className={className} />;
  if (code >= 61 && code <= 67) return <CloudRain className={className} />;
  if (code >= 71 && code <= 77) return <CloudSnow className={className} />;
  if (code >= 80 && code <= 82) return <CloudRain className={className} />;
  if (code >= 95 && code <= 99) return <CloudLightning className={className} />;
  return <Cloud className={className} />;
}

export default function GuestForecast() {
  const { t } = useTranslation();
  const [selectedDay, setSelectedDay] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: recipes } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => base44.entities.Recipe.list(),
    initialData: [],
  });

  // Fetch forecast from API
  const { data: forecastResponse, isLoading: forecastLoading } = useQuery({
    queryKey: ['forecast', 'guests', 7],
    queryFn: () => base44.forecast.getGuests(7),
    placeholderData: { forecasts: [], meta: {} },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const forecasts = forecastResponse?.forecasts || [];

  // Compute average guests from forecast data
  const nonClosedDays = forecasts.filter(f => !f.is_closed);
  const avgGuests = nonClosedDays.length > 0
    ? Math.round(nonClosedDays.reduce((sum, f) => sum + f.guests, 0) / nonClosedDays.length)
    : 0;

  // Transform API data into chart format
  const dayNamesShort = {
    'Montag': 'Mo', 'Dienstag': 'Di', 'Mittwoch': 'Mi',
    'Donnerstag': 'Do', 'Freitag': 'Fr', 'Samstag': 'Sa', 'Sonntag': 'So',
  };

  const forecastData = forecasts.map((f) => {
    // Determine bar color based on events and status
    let barColor = '#3b82f6'; // default blue
    if (f.is_closed) {
      barColor = '#94a3b8'; // gray for closed
    } else if (f.events && f.events.length > 0) {
      const impact = f.events[0].impact;
      if (impact === 'Sehr Positiv' || impact === 'Positiv') barColor = '#10b981';
      else if (impact === 'Negativ' || impact === 'Sehr Negativ') barColor = '#f59e0b';
    }

    return {
      day: dayNamesShort[f.day_name] || f.day_name.slice(0, 2),
      fullDay: f.day_name,
      guests: f.guests,
      baseGuests: f.base_guests,
      date: f.date,
      dayOfWeek: f.day_of_week,
      events: f.events || [],
      eventMultiplier: f.event_multiplier,
      isClosed: f.is_closed,
      fill: barColor,
      // New fields from API
      stddev: f.stddev,
      confidence: f.confidence,
      revenue: f.revenue,
      avgRevenuePerGuest: f.avg_revenue_per_guest,
      shifts: f.shifts || {},
      historicalCount: f.historical_count,
      weather: f.weather,
      weatherImpact: f.weather_impact,
    };
  });

  const handleDayClick = (data) => {
    setSelectedDay(data);
    setIsDialogOpen(true);
  };

  const getConfidenceBadge = (confidence) => {
    switch (confidence) {
      case 'high': return <Badge className="bg-green-100 text-green-700">{t("dashboard.confidenceHigh")}</Badge>;
      case 'medium': return <Badge className="bg-yellow-100 text-yellow-700">{t("dashboard.confidenceMedium")}</Badge>;
      case 'low': return <Badge className="bg-orange-100 text-orange-700">{t("dashboard.confidenceLow")}</Badge>;
      default: return <Badge className="bg-slate-100 text-slate-700">{t("dashboard.confidenceNone")}</Badge>;
    }
  };

  const calculatePreparationNeeds = (expectedGuests) => {
    if (recipes.length === 0 || expectedGuests === 0) return [];

    const totalDishes = Math.round(expectedGuests * 1.2);
    const activeRecipes = recipes.filter((recipe) => recipe.active !== false).slice(0, 10);
    const totalWeight = activeRecipes.reduce((sum, _, index) => sum + (activeRecipes.length - index), 0);

    const recommendations = activeRecipes
      .map((recipe, index) => {
        const weight = activeRecipes.length - index;
        const estimatedPortions = Math.max(1, Math.round((totalDishes * weight) / totalWeight));

        const ingredients = recipe.ingredients?.map(ing => ({
          name: ing.inventory_item_name,
          needed: (ing.quantity_needed * estimatedPortions).toFixed(2),
          unit: ing.unit
        })) || [];

        return {
          name: recipe.name,
          category: recipe.category,
          estimatedPortions,
          ingredients
        };
      });

    return recommendations;
  };

  const getEventImpactColor = (impact) => {
    switch(impact) {
      case 'Sehr Positiv': return 'bg-green-100 text-green-700 border-green-200';
      case 'Positiv': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Neutral': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'Negativ': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Sehr Negativ': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <>
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              {t("dashboard.guestForecast7Days")}
            </CardTitle>
            <div className="flex items-center gap-1 px-3 py-1 bg-blue-50 rounded-full">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-600">{t("dashboard.avgGuests", { count: avgGuests })}</span>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            <Sparkles className="w-3 h-3 inline mr-1" />
            {t("dashboard.clickDayForDetails")}
          </p>
        </CardHeader>
        <CardContent className="pt-0">
          {forecastLoading ? (
            <div className="h-[200px] flex items-center justify-center text-slate-400">
              {t("dashboard.loadingForecast")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={forecastData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="day"
                  tick={({ x, y, payload }) => {
                    const item = forecastData.find(d => d.day === payload.value);
                    const weatherCode = item?.weather?.weather_code;
                    return (
                      <g transform={`translate(${x},${y})`}>
                        <text x={0} y={0} dy={12} textAnchor="middle" fill="#64748b" fontSize={12}>
                          {payload.value}
                        </text>
                        {weatherCode != null && (
                          <foreignObject x={-8} y={14} width={16} height={16}>
                            <div className="flex items-center justify-center">
                              {getWeatherIcon(weatherCode, "w-3 h-3 text-slate-400")}
                            </div>
                          </foreignObject>
                        )}
                      </g>
                    );
                  }}
                  height={35}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <YAxis
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload[0]) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
                          <p className="font-semibold text-slate-900">{data.fullDay}</p>
                          <p className="text-sm text-slate-600">{data.guests} {t("dashboard.guests")} (+/-{data.stddev})</p>
                          {data.weather && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                              {getWeatherIcon(data.weather.weather_code, "w-3 h-3")}
                              <span>{data.weather.description} {data.weather.temp_max}C</span>
                            </div>
                          )}
                          {data.events.length > 0 && (
                            <p className="text-xs text-blue-600 mt-1">{data.events[0].name}</p>
                          )}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar
                  dataKey="guests"
                  radius={[8, 8, 0, 0]}
                  name={t("dashboard.guests")}
                  onClick={(data) => handleDayClick(data)}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-blue-500" />
              {selectedDay?.fullDay} - {selectedDay?.date}
              {selectedDay?.confidence && (
                <span className="ml-2">{getConfidenceBadge(selectedDay.confidence)}</span>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedDay && (
            <div className="space-y-6">
              {/* Events */}
              {selectedDay.events.length > 0 && (
                <div className="space-y-3">
                  {selectedDay.events.map((event, idx) => (
                    <Card key={idx} className={`border-2 ${getEventImpactColor(event.impact)}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles className="w-5 h-5" />
                              <h3 className="font-bold text-lg">{event.name}</h3>
                            </div>
                            <Badge className={getEventImpactColor(event.impact)}>{event.type}</Badge>
                            <Badge className="ml-2">{event.impact}</Badge>
                            {event.description && (
                              <p className="text-sm mt-2">{event.description}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold">
                              {event.guest_multiplier > 1 ? '+' : ''}
                              {((event.guest_multiplier - 1) * 100).toFixed(0)}%
                            </p>
                            <p className="text-xs">{t("dashboard.guestImpact")}</p>
                          </div>
                        </div>
                        {event.closed && (
                          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm font-semibold text-red-700">{t("dashboard.restaurantClosed")}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Guest Overview */}
              {!selectedDay.isClosed && (
                <>
                  <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-blue-100">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-blue-700 mb-1">{t("dashboard.expectedGuests")}</p>
                          <p className="text-4xl font-bold text-blue-900">{selectedDay.guests}</p>
                          <p className="text-sm text-blue-600 mt-1">+/- {selectedDay.stddev}</p>
                          {selectedDay.eventMultiplier !== 1.0 && (
                            <p className="text-xs text-blue-600 mt-2">
                              {t("dashboard.base")}: {selectedDay.baseGuests} x {selectedDay.eventMultiplier.toFixed(2)}
                            </p>
                          )}
                        </div>
                        <Users className="w-16 h-16 text-blue-300" />
                      </div>
                      {selectedDay.historicalCount > 0 && (
                        <p className="text-xs text-blue-700 mt-3">
                          {t("dashboard.basedOnHistoricalData", { count: selectedDay.historicalCount })}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Weather Conditions */}
                  {selectedDay.weather && (
                    <Card className="border-0 shadow-md">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-sky-50 rounded-lg">
                            {getWeatherIcon(selectedDay.weather.weather_code, "w-8 h-8 text-sky-500")}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900">{selectedDay.weather.description}</p>
                            <p className="text-sm text-slate-600">
                              {selectedDay.weather.temp_min}C - {selectedDay.weather.temp_max}C
                              {selectedDay.weather.precipitation_mm > 0 && (
                                <span className="ml-2">| {selectedDay.weather.precipitation_mm}mm</span>
                              )}
                            </p>
                          </div>
                          {selectedDay.weather.impact && (
                            <div className="text-right">
                              <Badge className="bg-sky-100 text-sky-700">
                                {selectedDay.weather.impact}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Rush Hours - Per-Shift Breakdown */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-orange-500" />
                      {t("dashboard.rushHours")}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {selectedDay.shifts && Object.keys(selectedDay.shifts).length > 0 ? (
                        Object.entries(selectedDay.shifts).map(([shiftName, guestCount]) => (
                          <Card key={shiftName} className="border-0 shadow-md">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <Badge className={shiftName === 'Mittag' ? "bg-yellow-100 text-yellow-700" : "bg-indigo-100 text-indigo-700"}>
                                  {shiftName === 'Mittag' ? t("dashboard.lunch") : shiftName === 'Abend' ? t("dashboard.dinner") : shiftName}
                                </Badge>
                                <span className="text-2xl font-bold text-slate-900">{guestCount}</span>
                              </div>
                              <div className="space-y-2 text-sm text-slate-600">
                                <div className="flex justify-between">
                                  <span>{t("dashboard.guests")}:</span>
                                  <span className="font-semibold">{guestCount}</span>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      ) : (
                        <>
                          <Card className="border-0 shadow-md">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <Badge className="bg-yellow-100 text-yellow-700">{t("dashboard.lunch")}</Badge>
                                <span className="text-2xl font-bold text-slate-900">{Math.round(selectedDay.guests * 0.4)}</span>
                              </div>
                              <p className="text-xs text-slate-400">{t("dashboard.estimatedSplit")}</p>
                            </CardContent>
                          </Card>
                          <Card className="border-0 shadow-md">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <Badge className="bg-indigo-100 text-indigo-700">{t("dashboard.dinner")}</Badge>
                                <span className="text-2xl font-bold text-slate-900">{Math.round(selectedDay.guests * 0.6)}</span>
                              </div>
                              <p className="text-xs text-slate-400">{t("dashboard.estimatedSplit")}</p>
                            </CardContent>
                          </Card>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Preparation Recommendations */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <ChefHat className="w-5 h-5 text-green-500" />
                      {t("dashboard.recommendedPreparation")}
                    </h3>
                    <div className="space-y-3">
                      {calculatePreparationNeeds(selectedDay.guests).map((rec, idx) => (
                        <Card key={idx} className="border-0 shadow-sm">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="font-semibold text-slate-900">{rec.name}</p>
                                <Badge variant="outline" className={`mt-1 text-xs ${getCategoryColorClass(rec.category)}`}>{rec.category}</Badge>
                              </div>
                              <span className="text-xl font-bold text-blue-600">{rec.estimatedPortions}x</span>
                            </div>
                            {rec.ingredients.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-slate-200">
                                <p className="text-xs font-semibold text-slate-600 mb-2">{t("dashboard.requiredIngredients")}:</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {rec.ingredients.map((ing, ingIdx) => (
                                    <div key={ingIdx} className="text-xs text-slate-600 flex justify-between">
                                      <span>{ing.name}</span>
                                      <span className="font-semibold">{ing.needed} {ing.unit}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}

                      {calculatePreparationNeeds(selectedDay.guests).length === 0 && (
                        <Card className="border-0 shadow-sm">
                          <CardContent className="text-center py-8">
                            <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                            <p className="text-slate-500 text-sm">
                              {t("dashboard.addRecipesForRecommendations")}
                            </p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>

                  {/* Historical Averages */}
                  {selectedDay.historicalCount > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-4">{t("dashboard.historicalAverages")}</h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        <Card className="border-0 shadow-sm">
                          <CardContent className="p-4">
                            <p className="text-xs text-slate-500 mb-1">{t("dashboard.forecastRevenue")}</p>
                            <p className="text-2xl font-bold text-slate-900">
                              EUR {selectedDay.revenue?.toFixed(2) || '0.00'}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="border-0 shadow-sm">
                          <CardContent className="p-4">
                            <p className="text-xs text-slate-500 mb-1">{t("dashboard.avgPerGuest")}</p>
                            <p className="text-2xl font-bold text-slate-900">
                              EUR {selectedDay.avgRevenuePerGuest?.toFixed(2) || '0.00'}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="border-0 shadow-sm">
                          <CardContent className="p-4">
                            <p className="text-xs text-slate-500 mb-1">{t("dashboard.dataPoints")}</p>
                            <p className="text-2xl font-bold text-slate-900">{selectedDay.historicalCount}</p>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
